import React, { useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Footer, GradientHero, SEO, blogPostingSchema, breadcrumbSchema } from '@coupling/ui';
import { getBlogPost } from '../utils/blog';
import Header from '../components/Header';
import { seoConfig } from '../config/seo';

const BlogPost: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();

    const post = useMemo(() => {
        if (!slug) return undefined;
        return getBlogPost(slug);
    }, [slug]);

    if (!post) {
        return <Navigate to="/blog" replace />;
    }

    return (
        <div className="min-h-screen bg-white font-sans text-neutral-900">
            <SEO
                title={`${post.title} - ${seoConfig.siteName} Blog`}
                description={post.description}
                canonical={`${seoConfig.siteUrl}/blog/${slug}`}
                ogImage={seoConfig.defaultOgImage}
                ogUrl={`${seoConfig.siteUrl}/blog/${slug}`}
                jsonLd={[
                    blogPostingSchema({
                        title: post.title,
                        description: post.description,
                        author: post.author,
                        datePublished: post.date,
                        url: `${seoConfig.siteUrl}/blog/${slug}`,
                    }),
                    breadcrumbSchema([
                        { name: 'Home', url: seoConfig.siteUrl },
                        { name: 'Blog', url: `${seoConfig.siteUrl}/blog` },
                        { name: post.title, url: `${seoConfig.siteUrl}/blog/${slug}` },
                    ]),
                ]}
            />
            <Header />

            <GradientHero
                title={post.title}
                description={`By ${post.author} \u2022 ${post.date}`}
            />

            <main className="max-w-3xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <Link to="/blog" className="text-brand-primary hover:text-brand-primary-dark inline-block">
                        &larr; Back to Blog
                    </Link>
                </div>

                <article className="prose prose-lg prose-neutral mx-auto text-neutral-700">
                    <ReactMarkdown
                        components={{
                            a: ({ href, children, ...props }) => {
                                if (href && href.startsWith('/')) {
                                    return <Link to={href}>{children}</Link>;
                                }
                                return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
                            },
                        }}
                    >{post.content}</ReactMarkdown>
                </article>
            </main>

            <Footer />
        </div>
    );
};

export default BlogPost;
